import { AnimatePresence } from "framer-motion";
import { ScaleIcon, SparklesIcon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { AdminChat } from "@/features/ai-chat/components/admin-chat";
import { useClaim } from "@/features/claims/api/use-claim";
import { ClaimActionsMenu } from "@/features/claims/components/claim-actions-menu";
import { ClaimSteps } from "@/features/claims/components/claim-steps";
import { AcquisitionHistoryTab } from "@/features/claims/tabs/acquisition-history-tab";
import { OverviewTab } from "@/features/claims/tabs/overview-tab";
import { PassengersTab } from "@/features/claims/tabs/passengers-tab";
import { TimelineTab } from "@/features/claims/tabs/timeline-tab";
import { useCurrentUser } from "@/hooks/use-current-user";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import { AffaireFlyout } from "./affaire-flyout";

export const CLAIM_PREVIEW_TABS = [
  "overview",
  "timeline",
  "acquisition-history",
  "passengers",
  "assistant",
] as const;
export type ClaimPreviewTab = (typeof CLAIM_PREVIEW_TABS)[number];

/** Same labels and order as the claim detail TopMenu (ClaimHeader / Vue TopMenu.vue). */
const TAB_LABELS: Record<ClaimPreviewTab, string> = {
  overview: "Dossier",
  timeline: "Timeline",
  "acquisition-history": "Feedback",
  passengers: "Réservation",
  assistant: "Assistant",
};

/** Onglets rendus en icône seule : une action sur le dossier, pas une vue. */
const ICON_ONLY_TABS: Partial<Record<ClaimPreviewTab, typeof SparklesIcon>> = {
  assistant: SparklesIcon,
};

interface ClaimPreviewPanelProps {
  claimId: string;
  activeTab: ClaimPreviewTab;
  onTabChange: (tab: ClaimPreviewTab) => void;
  onClose: () => void;
}

/**
 * Right-side claim preview of the tasks/signatures/claims pages — React
 * counterpart of Tasks/ClaimPreviewLayout.vue, driven by the ?claim/?claimTab
 * search params. The tab bar reproduces the Vue TopMenu (blue-900) styling.
 * The overview tab renders the FULL overview (OverviewTab — flight card,
 * banners, CTAs, everything), exactly like the Vue pane did.
 */
export function ClaimPreviewPanel({ claimId, activeTab, onTabChange, onClose }: ClaimPreviewPanelProps) {
  const { data: claim } = useClaim(claimId);
  const { can } = useCurrentUser();
  const panelRef = useRef<HTMLDivElement>(null);
  // Le dossier dont l'affaire est ouverte, pas un booléen : passer d'un dossier à
  // l'autre referme l'affaire du précédent sans effet de reset.
  const [affaireClaimId, setAffaireClaimId] = useState<string | null>(null);
  const affaireOpen = affaireClaimId === claimId;

  // Suivi rapatrié depuis la page pleine /claims/$claimId/timeline, qui redirige désormais ici.
  useEffect(() => {
    if (activeTab === "timeline") {
      track("Timeline Opened", { claimId });
    }
  }, [activeTab, claimId]);

  // Une affaire n'existe qu'une fois le dossier enrôlé (numéro RG) : sans lui, le
  // panneau n'aurait rien à afficher.
  const procedureId = claim?.procedure?.identifier ? claim.procedure.id : null;

  // Miroir du gate API (POST /admin/ai/chat, 403 hors rôles internes) : ne pas
  // afficher un onglet qui ne peut qu'échouer. L'affaire prend sa place quand il
  // y en a une — la barre n'a pas la largeur pour les deux icônes. L'affaire est
  // réservée aux mêmes rôles internes.
  const canUseAssistant = can([...UPCLAIM_ROLES, "LAWYER"]);
  const visibleTabs = CLAIM_PREVIEW_TABS.filter(
    (tab) => tab !== "assistant" || (canUseAssistant && !procedureId),
  );

  return (
    <div ref={panelRef} className="flex h-full flex-col overflow-hidden">
      {/* Tab bar — Vue TopMenu.vue styling (dark-blue Upclaim bar). */}
      <nav className="shrink-0 border-b border-gray-200 bg-blue-900 pl-2 pr-4 text-white">
        <div className="flex h-12 items-center space-x-2">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              aria-current={activeTab === tab ? "page" : undefined}
              onClick={() => onTabChange(tab)}
              aria-label={TAB_LABELS[tab]}
              title={TAB_LABELS[tab]}
              className={cn(
                "relative rounded-md px-3 py-2 text-sm font-medium text-gray-100 hover:bg-blue-700 hover:text-white",
                activeTab === tab && "bg-blue-700 text-white hover:bg-blue-600",
              )}
            >
              {(() => {
                const Icon = ICON_ONLY_TABS[tab];
                return Icon ? <Icon className="size-4" aria-hidden /> : TAB_LABELS[tab];
              })()}
            </button>
          ))}
          {procedureId && canUseAssistant ? (
            <button
              type="button"
              aria-pressed={affaireOpen}
              onClick={() => setAffaireClaimId(affaireOpen ? null : claimId)}
              aria-label="Affaire"
              title="Affaire"
              className={cn(
                "relative rounded-md px-3 py-2 text-sm font-medium text-gray-100 hover:bg-blue-700 hover:text-white",
                affaireOpen && "bg-blue-700 text-white hover:bg-blue-600",
              )}
            >
              <ScaleIcon className="size-4" aria-hidden />
            </button>
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            {/* Actions dropdown right-aligned on the dark bar, like the full ClaimHeader TopMenu. */}
            {claim ? <ClaimActionsMenu claim={claim} /> : null}
            <button
              type="button"
              aria-label="Fermer l'aperçu"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-300 hover:bg-blue-700 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      {claim ? <ClaimSteps claim={claim} /> : null}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {activeTab === "overview" ? <OverviewTab claimId={claimId} /> : null}
        {activeTab === "timeline" ? <TimelineTab claimId={claimId} variant="compact" /> : null}
        {activeTab === "passengers" ? <PassengersTab claimId={claimId} variant="compact" /> : null}
        {activeTab === "acquisition-history" ? (
          <AcquisitionHistoryTab claimId={claimId} variant="compact" />
        ) : null}
        {activeTab === "assistant" && canUseAssistant ? (
          <div className="h-full p-3">
            <AdminChat claimId={claimId} claimNumber={claim?.claimNumber ?? null} />
          </div>
        ) : null}
      </div>

      <AnimatePresence>
        {procedureId && affaireOpen ? (
          <AffaireFlyout
            procedureId={procedureId}
            anchorRef={panelRef}
            onClose={() => setAffaireClaimId(null)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
